/* =========================================================
   AS Piscine — motion kit for the Dawn skin
   custom cursor · magnetic cursor-glow on buttons · blur-reveal
   Exposes window.ASP.bindMotion(root) for dynamically added nodes.
   ========================================================= */
window.ASP = window.ASP || {};
(() => {
  "use strict";
  const fine = matchMedia("(hover: hover) and (pointer: fine)").matches;
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const lerp = (a, b, n) => a + (b - a) * n;
  const ring = document.querySelector(".asp-cursor");

  if (fine && !reduced && ring) {
    document.documentElement.classList.add("asp-cursor-on");
    const dot = document.querySelector(".asp-cursor-dot");
    let mx = innerWidth / 2, my = innerHeight / 2, rx = mx, ry = my;

    /* Cross-origin embeds (Google Maps, Calendly) swallow mousemove — the PARENT
       document stops receiving it — so a cursor that just follows the pointer would
       freeze at the embed's edge and read as lag. Instead of cutting out hard, the
       cursor RETREATS as it approaches: the ring scales down and is gone just before
       the boundary, leaving only the dot, and the dot shrinks to nothing exactly at
       the edge. Reverses on the way out. Opt in with data-asp-native-cursor. */
    const nativeZones = [...document.querySelectorAll("[data-asp-native-cursor]")];
    const RING_FADE = 140;   // ring at full size beyond this
    const RING_GONE = 16;    // ring fully retracted here — only the dot is left
    const DOT_GONE  = 16;    // dot shrinks across this last stretch, 0 at the edge
    const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);

    /* Measured fresh, NOT cached. The first version cached the rects and invalidated
       on scroll/resize — but a LAYOUT SHIFT fires neither, so once the Calendly iframe
       and the images finished loading and pushed the map down the page, the cursor was
       being compared against coordinates from before the page settled and never
       retreated at all. Two getBoundingClientRects per event is cheap; the thing that
       is NOT cheap is interleaving reads with writes, so every read happens first and
       every style write second. */
    const distToZones = (x, y) => {
      let best = Infinity;
      for (const el of nativeZones) {
        const r = el.getBoundingClientRect();
        const dx = Math.max(r.left - x, 0, x - r.right);
        const dy = Math.max(r.top - y, 0, y - r.bottom);
        const d = Math.hypot(dx, dy);
        if (d < best) best = d;
      }
      return best;
    };

    /* The scale MUST live inside the transform string, appended after the translates —
       not in the independent `scale` longhand. The longhands are applied AFTER
       transform in the matrix chain (translate → rotate → scale → transform), so
       `scale` there shrinks the element about its ORIGINAL origin, which for these
       fixed top:0/left:0 elements sits in the viewport's top-left corner: the cursor
       visibly slid off toward that corner as it shrank instead of imploding in place.
       Written last in the transform it is the innermost operation, so the element
       collapses on its own centre and is then translated to the pointer.
       Safe to share the transform slot here because this code owns every write to it. */
    addEventListener("mousemove", (e) => {
      mx = e.clientX; my = e.clientY;
      const d = nativeZones.length ? distToZones(mx, my) : Infinity;   // read
      const s = clamp01(d / DOT_GONE);
      dot.style.transform = `translate(${mx}px, ${my}px) translate(-50%, -50%) scale(${s})`;  // then write
    });

    (function loop() {
      rx = lerp(rx, mx, 0.18); ry = lerp(ry, my, 0.18);
      /* measured from the RING's own trailing position, not the pointer's, so the
         shrink matches where the ring actually is on screen */
      const d = nativeZones.length ? distToZones(rx, ry) : Infinity;   // read
      const s = clamp01((d - RING_GONE) / (RING_FADE - RING_GONE));
      ring.style.transform = `translate(${rx}px, ${ry}px) translate(-50%, -50%) scale(${s})`;  // then write
      requestAnimationFrame(loop);
    })();

    /* Inside the embed the native cursor takes over (our ring is already at 0 there). */
    const setNative = (on) => document.documentElement.classList.toggle("asp-cursor-native", on);
    nativeZones.forEach((el) => {
      el.addEventListener("mouseenter", () => setNative(true));
      el.addEventListener("mouseleave", () => setNative(false));
    });
  }

  /* Reveal trigger. The desktop numbers (12% of the element visible, bottom edge
     pulled up 8%) are wrong on a phone and were the cause of "panels appear out of
     nowhere, they don't animate": once a section stacks to one column, each panel is
     ~half the viewport tall, so 12% of it is a ~50px sliver. Measured at 390×844, a
     422px .h-arch fired with its top at y=726 — i.e. 118px of it on screen, almost
     entirely below the fold. The 0.9s animation then ran off-screen and was over
     before the visitor scrolled to it.
     On mobile we switch to an edge trigger instead of a ratio one: threshold 0 with
     the root's bottom pulled up 32% fires the moment any part of the element crosses
     y = 0.68 × viewport height. Height-independent, and it starts the animation with
     ~270px of a 422px panel already on screen, so it plays where the eye is instead
     of below the fold. Paired with a shorter reveal on mobile (asp-theme.css) — a
     0.9s desktop-length fade is easy to flick straight past on a phone. */
  const mob = matchMedia("(max-width: 749px)").matches;
  const revealIO = "IntersectionObserver" in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setTimeout(() => en.target.classList.add("is-in"), en.target.dataset.revealDelay || 0);
            revealIO.unobserve(en.target);
          }
        });
      }, mob
        ? { threshold: 0, rootMargin: "0px 0px -32% 0px" }
        : { threshold: 0.12, rootMargin: "0px 0px -8% 0px" })
    : null;

  /* Safety net for the mobile trigger: an element sitting in the bottom 32% of the
     LAST scroll position can never cross the trigger line, and would stay at
     opacity 0 forever. When we reach the end of the document, reveal whatever is
     still hidden. Cheap (one listener, self-removing once nothing is left). */
  if (mob && revealIO) {
    const sweep = () => {
      if (innerHeight + scrollY < document.documentElement.scrollHeight - 4) return;
      const left = document.querySelectorAll(".asp-reveal:not(.is-in), .asp-reveal-group:not(.is-in)");
      left.forEach((el) => { revealIO.unobserve(el); el.classList.add("is-in"); });
      if (!left.length) removeEventListener("scroll", sweep);
    };
    addEventListener("scroll", sweep, { passive: true });
  }

  function bindMotion(root = document) {
    if (fine && !reduced && ring) {
      root.querySelectorAll("a, button, .button, .asp-magnetic").forEach((el) => {
        if (el.dataset.aspCursor) return;
        el.dataset.aspCursor = "1";
        el.addEventListener("mouseenter", () => ring.classList.add("is-hover"));
        el.addEventListener("mouseleave", () => ring.classList.remove("is-hover"));
      });
      // cursor-tracking glow on buttons / magnetic elements
      /* .shoplan__body added 2026-09-10 — the Magazin landing's text strip takes the same
         cursor-follow glow as the CTAs. It is the one host in this list that is NOT a control;
         it is hoverable as part of its card, so the glow tracks while the pointer is anywhere
         over the strip and the CSS reveals it on `.shoplan__card:hover`. */
      root.querySelectorAll(".button, .asp-magnetic, .asp-cta, .asp-cart, .quantity__button, .asp-related__arrow, .shoplan__body").forEach((el) => {
        if (el.dataset.aspGlow) return;
        el.dataset.aspGlow = "1";
        el.style.position = el.style.position || "relative";
        /* Seed the position on ENTER, synchronously. Without this the first painted frame uses
           whatever --gx/--gy held: on a first-ever hover that is the CSS default (50% 50%, i.e.
           parked dead centre) and on a repeat hover it is wherever the pointer LEFT last time.
           mousemove alone cannot fix it — it only fires once the pointer has already moved, and
           it defers to rAF, so the wrong frame is on screen first. Cheap and un-throttled
           because it fires once per enter, not per pixel. */
        el.addEventListener("mouseenter", (e) => {
          const r = el.getBoundingClientRect();
          el.style.setProperty("--gx", `${e.clientX - r.left}px`);
          el.style.setProperty("--gy", `${e.clientY - r.top}px`);
        });
        let gRaf = false;
        el.addEventListener("mousemove", (e) => {
          if (gRaf) return;
          gRaf = true;
          const cx = e.clientX, cy = e.clientY;
          requestAnimationFrame(() => {
            const r = el.getBoundingClientRect();
            el.style.setProperty("--gx", `${cx - r.left}px`);
            el.style.setProperty("--gy", `${cy - r.top}px`);
            gRaf = false;
          });
        });
      });
    }
    /* .asp-reveal-group is observed exactly like .asp-reveal — same class toggle,
       same observer. The difference is entirely in CSS: the group's `is-in` styles
       its CHILDREN and staggers them by :nth-child, so a container of any size
       cascades without a per-card attribute. See asp-theme.css. */
    const reveals = root.querySelectorAll(".asp-reveal:not(.is-in), .asp-reveal-group:not(.is-in)");
    if (reduced || !revealIO) reveals.forEach((el) => el.classList.add("is-in"));
    else reveals.forEach((el) => revealIO.observe(el));
  }

  /* ---------- Header interactions ---------- */
  function initHeader() {
    const header = document.querySelector(".asp-header");
    if (header) {
      let hovering = false;
      const update = () => header.classList.toggle("is-scrolled", scrollY > 40 || hovering);
      addEventListener("scroll", update, { passive: true });
      // Shrink-on-hover without the edge-chasing jitter. The old mouseenter/mouseleave
      // listened on the header box itself — but hovering shrinks that box (padding 18->12),
      // so its bottom edge retreated off the cursor -> leave -> grow -> enter -> oscillation.
      // Instead we treat "hovering" as: pointer within a FIXED band = the header's TALL
      // height. Because the band never moves when the header shrinks, the shrink pushes the
      // real edge AWAY from the pointer instead of out from under it, so the loop can't start.
      // Only on hover-capable devices; touch has no hover and scroll still drives the shrink.
      if (matchMedia("(hover: hover) and (pointer: fine)").matches) {
        let band = header.offsetHeight;                                    // tall height
        const remeasure = () => { if (!hovering && scrollY <= 40) band = header.offsetHeight; };
        addEventListener("resize", remeasure, { passive: true });
        addEventListener("load", remeasure);
        addEventListener("pointermove", (e) => {
          let over = e.clientY <= band;
          // Keep it shrunk while the cursor is down inside an open panel (below the band).
          // Any of the three counts: un-shrinking mid-interaction grows the header by 6px and
          // shoves the open panel (top: 100%) down with it.
          if (!over && hovering) {
            over = !!header.querySelector(".asp-nav__item--menu:hover, .asp-search.is-open, .asp-cartpanel.active");
          }
          if (over !== hovering) { hovering = over; update(); }
        }, { passive: true });
      }
      update();
    }
    const burger = document.querySelector(".asp-burger");
    if (burger && header) {
      /* ---- side-drawer open/close (2026-07-27) ----
         The exit needs the panel to still LOOK like a panel while it slides out, so closing
         keeps `is-open` and adds `is-closing`; both come off when the animation ends. Dropping
         `is-open` first would snap the drawer back to an 86px header bar in one frame. */
      const DRAWER_MS = 340;
      let closeT = null;
      const openMenu = () => {
        clearTimeout(closeT);
        header.classList.remove("is-closing");
        header.classList.add("is-open");
        burger.setAttribute("aria-expanded", "true");
        document.body.style.overflow = "hidden";
      };
      const closeMenu = () => {
        if (!header.classList.contains("is-open") || header.classList.contains("is-closing")) return;
        burger.setAttribute("aria-expanded", "false");
        document.body.style.overflow = "";
        const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
        header.classList.add("is-closing");
        clearTimeout(closeT);
        closeT = setTimeout(() => {
          header.classList.remove("is-open", "is-closing");
          collapseAll();   // reset the accordion so it never reopens mid-section
        }, reduce ? 0 : DRAWER_MS);
      };
      burger.addEventListener("click", () => {
        header.classList.contains("is-open") && !header.classList.contains("is-closing")
          ? closeMenu()
          : openMenu();
      });
      /* tap the dimmed page to dismiss. The dim is `.asp-header::before`, and a pseudo-element
         hit-tests as its HOST — so a tap on it arrives here with `.asp-header` as the target and
         nothing from the panel in the path. */
      header.addEventListener("click", (e) => {
        if (!header.classList.contains("is-open")) return;
        if (e.target === header) closeMenu();
      });
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") closeMenu();
      });
      /* `:not(.asp-nav__toggle)` — the 3 expandable rows must NOT close the drawer, they open
         a section inside it. On desktop they are still ordinary links and closeMenu is a no-op
         there (the header is never `is-open`), so excluding them costs nothing. */
      header.querySelectorAll(".asp-nav a:not(.asp-nav__toggle)").forEach((a) => a.addEventListener("click", closeMenu));

      /* ---- mobile drawer accordion (2026-07-27) ----
         EXCLUSIVE, like the store rail: two open sections read as a mistake and push the rest
         of the nav off-screen, which is the problem this rebuild exists to fix. Gated on the
         same 989px query the CSS uses — above it these are real links and hover owns the menu. */
      const mqNav = window.matchMedia("(max-width: 989px)");
      header.querySelectorAll(".asp-nav__toggle").forEach((t) => {
        t.addEventListener("click", (e) => {
          if (!mqNav.matches) return;               // desktop → let it navigate
          e.preventDefault();
          const item = t.closest(".asp-nav__item--menu");
          if (!item) return;
          const willOpen = !item.classList.contains("is-open");
          header.querySelectorAll(".asp-nav__item--menu").forEach((i) => i.classList.remove("is-open"));
          item.classList.toggle("is-open", willOpen);
          t.setAttribute("aria-expanded", willOpen ? "true" : "false");
        });
      });
      /* closing the drawer resets it, so it never reopens mid-section. Called from closeMenu's
         teardown timer — it can no longer key off "is the header closed?", because the header
         deliberately stays `is-open` for the length of the exit animation. */
      const collapseAll = () => header.querySelectorAll(".asp-nav__item--menu").forEach((i) => {
        i.classList.remove("is-open");
        const t = i.querySelector(".asp-nav__toggle");
        if (t) t.setAttribute("aria-expanded", "false");
      });
      mqNav.addEventListener("change", (m) => { if (!m.matches) collapseAll(); });
    }
    document.querySelectorAll("[data-scroll-top]").forEach((el) => {
      el.addEventListener("click", (e) => { e.preventDefault(); scrollTo({ top: 0, behavior: "smooth" }); });
    });
    /* ---- cart panel: the third header dropdown, dismissed like the other two ----
       Opening/closing itself lives in cart-drawer.js (Dawn's custom element owns the `active`
       class). Here we only add the dismissal rules the mega menus and search already follow:
       Esc, a click outside the panel + its trigger, and reaching for a nav menu. */
    const cartPanel = document.getElementById("aspCartPanel");
    const cartPill = document.getElementById("cart-icon-bubble");
    if (cartPanel && cartPill) {
      const cartOpen = () => cartPanel.classList.contains("active");
      const closeCart = () => { if (cartOpen()) cartPanel.close(); };
      document.addEventListener("click", (e) => {
        if (cartOpen() && !cartPanel.contains(e.target) && !cartPill.contains(e.target)) closeCart();
      });
      document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeCart(); });
      document.querySelectorAll(".asp-nav__item--menu").forEach((item) => {
        item.addEventListener("mouseenter", closeCart);
        item.addEventListener("focusin", closeCart);
      });
      /* the search panel and the cart panel occupy the same slot under the header — one opening
         is an implicit dismissal of the other (search dismisses cart via its own openS below) */
      const sBtnEl = document.getElementById("aspSearchBtn");
      if (sBtnEl) sBtnEl.addEventListener("click", closeCart);
    }

    /* predictive search overlay */
    const sBtn = document.getElementById("aspSearchBtn");
    const sBox = document.getElementById("aspSearch");
    const sClose = document.getElementById("aspSearchClose");
    const sInput = document.getElementById("aspSearchInput");
    const sResults = document.getElementById("aspSearchResults");
    if (sBtn && sBox) {
      const openS = () => { header.classList.remove("is-open"); document.body.style.overflow = ""; sBox.classList.add("is-open"); sBox.setAttribute("aria-hidden", "false"); if (sInput) setTimeout(() => sInput.focus(), 60); };
      const closeS = () => { sBox.classList.remove("is-open"); sBox.setAttribute("aria-hidden", "true"); };
      sBtn.addEventListener("click", (e) => { e.stopPropagation(); sBox.classList.contains("is-open") ? closeS() : openS(); });
      if (sClose) sClose.addEventListener("click", closeS);
      /* dropdown, not an overlay — close on click outside the panel + trigger */
      document.addEventListener("click", (e) => { if (sBox.classList.contains("is-open") && !sBox.contains(e.target) && !sBtn.contains(e.target)) closeS(); });
      document.addEventListener("keydown", (e) => { if (e.key === "Escape" && sBox.classList.contains("is-open")) closeS(); });
      /* the nav mega menus open on hover and would otherwise sit stacked over the open search
         panel — reaching for one is an implicit dismissal of the other. focusin covers keyboard. */
      const dismissS = () => { if (sBox.classList.contains("is-open")) closeS(); };
      document.querySelectorAll(".asp-nav__item--menu").forEach((item) => {
        item.addEventListener("mouseenter", dismissS);
        item.addEventListener("focusin", dismissS);
      });
      /* cart panel shares this slot under the header — opening it puts search away */
      if (cartPill) cartPill.addEventListener("click", dismissS);

      if (sInput && sResults) {
        const fmt = (p) => { const n = parseFloat(p); return isNaN(n) ? "" : n.toLocaleString("ro-RO", { style: "currency", currency: "RON" }); };
        const esc = (s) => (s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
        let timer, ctrl;
        /* Swap results and animate the panel's growth. Height is measured, not guessed: the grid
           is auto-height, so we read the outgoing box, write the new markup, read the natural
           height, then tween between the two. Uses WAAPI so nothing is left inline afterwards —
           the element returns to `height: auto` on its own once the run finishes. marginTop rides
           along because :empty collapses it, which would otherwise snap 28px mid-animation. */
        function render(html) {
          const fromH = sResults.getBoundingClientRect().height;
          const fromM = getComputedStyle(sResults).marginTop;
          sResults.innerHTML = html;
          if (reduced) return;
          sResults.querySelectorAll(".asp-search__tile").forEach((el, i) => {
            el.style.animationDelay = Math.min(i * 45, 400) + "ms";
          });
          const toH = sResults.scrollHeight;
          const toM = getComputedStyle(sResults).marginTop;
          if (Math.abs(toH - fromH) < 2) return;
          sResults.style.overflow = "hidden";
          const anim = sResults.animate(
            [{ height: fromH + "px", marginTop: fromM }, { height: toH + "px", marginTop: toM }],
            { duration: 420, easing: "cubic-bezier(.22,1,.36,1)" }
          );
          anim.onfinish = anim.oncancel = () => { sResults.style.overflow = ""; };
        }
        function fetchResults(q) {
          if (!q || q.length < 2) { render(""); return; }
          if (ctrl) ctrl.abort();
          ctrl = new AbortController();
          fetch("/search/suggest.json?q=" + encodeURIComponent(q) + "&resources[type]=product&resources[limit]=12&resources[options][unavailable_products]=last", { signal: ctrl.signal })
            .then((r) => r.json())
            .then((d) => {
              const prods = (d.resources && d.resources.results && d.resources.results.products) || [];
              if (!prods.length) { render('<p class="asp-search__empty">Niciun produs găsit pentru „' + esc(q) + '".</p>'); return; }
              render(prods.map((p) => {
                const raw = (p.featured_image && p.featured_image.url) || p.image || "";
                const img = raw ? (raw + (raw.indexOf("?") > -1 ? "&" : "?") + "width=300") : "";
                return '<a class="asp-search__tile" href="' + p.url + '">' +
                  '<div class="asp-search__tile-img">' + (img ? '<img src="' + img + '" loading="lazy" alt="">' : "") + "</div>" +
                  '<div class="asp-search__tile-body"><div class="asp-search__tile-name">' + esc(p.title) + "</div>" +
                  (p.price ? '<div class="asp-search__tile-price">' + fmt(p.price) + "</div>" : "") + "</div></a>";
              }).join(""));
            })
            .catch(() => {});
        }
        sInput.addEventListener("input", () => { clearTimeout(timer); timer = setTimeout(() => fetchResults(sInput.value.trim()), 220); });
      }
    }
  }

  /* ---------- Hero parallax ---------- */
  function initParallax() {
    // desktop-only flourish; on touch it just causes scroll jank
    if (reduced || !fine) return;
    const layers = document.querySelectorAll("[data-parallax]");
    if (!layers.length) return;
    layers.forEach((l) => { l.style.willChange = "transform"; });
    let ticking = false;
    addEventListener("scroll", () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = scrollY;
        layers.forEach((l) => { l.style.transform = `translateY(${y * parseFloat(l.dataset.parallax)}px)`; });
        ticking = false;
      });
    }, { passive: true });
  }

  /* ---------- PDP gallery: animate the media-frame height on image swap ----------
     Dawn swaps the main photo by display-toggling the active <li> (media-gallery.js
     setActiveMedia), so the viewer's height SNAPS from the old photo's aspect to the new
     one's — and since the media column drives the product-row height on image-rich products,
     everything from Descriere down jumps. This FLIPs it: grab the height BEFORE Dawn swaps
     (capture-phase click, which runs ahead of Dawn's own handler), then transition the viewer
     old→new so the page grows/shrinks smoothly. The top edge stays anchored (only the bottom
     travels); the coupled info column (buy + shipping) rides along via the flex stretch. */
  function initPdpGallery() {
    const wrapper = document.querySelector(".product__media-wrapper");
    if (!wrapper) return;
    const viewer = wrapper.querySelector("media-gallery > slider-component:not(.thumbnail-slider)");
    const thumbs = wrapper.querySelector(".thumbnail-slider");
    if (!viewer || !thumbs) return;

    /* Animate the WHITE PLATE (.product__media), not the outer slider-component. Animating the
       viewer needed `overflow:hidden` to clip a growing photo — and that creates a block
       formatting context, which stops `.product__media-list`'s 20px bottom margin from collapsing
       out (measured: viewer 311px with it vs 291px natural). Pinned inside a fixed height that
       20px got swallowed, so the image→thumbnail gap collapsed 21px→1px mid-animation and then
       snapped back on cleanup — the thumbnails stuck to the photo, then jumped.
       The plate owns its height via `padding-top: var(--ratio-percent)`; swapping that for an
       explicit animated height keeps it in normal flow, so the viewer, thumbnails and everything
       below follow naturally — no overflow, no BFC, no snap. The photo is absolutely positioned
       at height:100% with object-fit:contain, so it stays fitted the whole way. (2026-07-27) */
    const activePlate = () => viewer.querySelector(".product__media-item.is-active .product__media");
    let fromH = null, clearT, running = null;

    const reset = (el) => {
      if (!el) return;
      el.style.height = ""; el.style.paddingTop = ""; el.style.transition = "";
      const im = el.querySelector("img");
      if (im) { im.style.transition = ""; im.style.opacity = ""; }
    };

    // capture phase → fires before Dawn's click handler mutates the DOM
    thumbs.addEventListener("click", (e) => {
      if (!e.target.closest(".thumbnail-list__item")) return;
      const p = activePlate();
      fromH = p ? p.getBoundingClientRect().height : null;
    }, true);

    // Dawn toggles .is-active synchronously; the observer callback runs before the next paint,
    // so we can pin the old height before the snapped state is ever shown (no flash).
    new MutationObserver(() => {
      if (fromH == null) return;
      const from = fromH; fromH = null;
      if (reduced) return;
      const plate = activePlate();
      if (!plate) return;
      clearTimeout(clearT);
      reset(running);                                  // tidy an interrupted previous run
      const to = plate.getBoundingClientRect().height; // natural new height, before we override it
      if (Math.abs(to - from) < 2) { running = null; return; }  // same aspect → nothing to animate
      running = plate;
      plate.style.transition = "none";
      plate.style.paddingTop = "0px";                  // release the intrinsic ratio height
      plate.style.height = from + "px";
      plate.getBoundingClientRect();                   // commit `from` before transitioning
      plate.style.transition = "height .42s var(--asp-ease, ease)";
      plate.style.height = to + "px";
      // soften the instant content swap; keep the transform term so the hover-zoom still eases
      const img = plate.querySelector("img");
      if (img) {
        img.style.transition = "none"; img.style.opacity = "0";
        requestAnimationFrame(() => {
          img.style.transition = "opacity .34s var(--asp-ease, ease), transform .6s var(--asp-ease, ease)";
          img.style.opacity = "1";
        });
      }
      clearT = setTimeout(() => { reset(plate); running = null; }, 470);
    }).observe(viewer, { attributes: true, attributeFilter: ["class"], subtree: true });
  }

  /* ---------- Side-scroll rails: reveal the whole rail at once ----------
     Rail children are .asp-reveal, and a card scrolled off to the RIGHT does not
     intersect the viewport — so cards 3/4/5 would have sat at opacity 0 until the
     visitor swiped to them, each then fading in under its own stagger delay. That
     reads as broken content, not as motion. Instead: stop observing the cards
     individually and cascade the whole set when the RAIL crosses the trigger line.
     Mobile only — on desktop these are grids and every card is genuinely in view.

     Only needed for rails whose cards carry .asp-reveal INDIVIDUALLY — i.e. the
     landing page, hand-tagged before .asp-reveal-group existed. A rail marked
     .asp-reveal-group has no .asp-reveal children, so `kids` comes back empty and
     this skips it: the group already cascades from the container, at every width. */
  function initRailReveals() {
    if (!mob || !revealIO) return;
    document.querySelectorAll(".asp-sidescroll").forEach((rail) => {
      const kids = [...rail.querySelectorAll(".asp-reveal:not(.is-in)")];
      if (!kids.length) return;
      kids.forEach((k) => revealIO.unobserve(k));
      const railIO = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          kids.forEach((k) => setTimeout(() => k.classList.add("is-in"), k.dataset.revealDelay || 0));
          railIO.disconnect();
        });
      }, { threshold: 0, rootMargin: "0px 0px -32% 0px" });
      railIO.observe(rail);
    });
  }

  /* ---------- Side-scroll rails: resting position on load ----------
     Opt in with data-ss-start="<index>" on a track carrying .asp-sidescroll.
     Only runs where the track is actually a horizontal scroller (mobile), and
     only sets scrollLeft on the TRACK — never the page. Hidden children (e.g.
     the desktop-only configurator card) are skipped so the index still refers
     to what the visitor can see. */
  function initSidescroll() {
    /* Mobile only. `.c-track` is a horizontal scroller on DESKTOP too, so a
       scrollWidth>clientWidth test is not enough to tell "this is a phone rail"
       apart from "this is the wide rail" — without this gate the desktop rail
       loaded scrolled 110px in, clipping the first card. */
    if (!mob) return;
    document.querySelectorAll(".asp-sidescroll[data-ss-start]").forEach((track) => {
      if (track.scrollWidth <= track.clientWidth + 4) return;   // not a rail here
      const kids = [...track.children].filter((k) => k.offsetParent !== null);
      const kid = kids[parseInt(track.dataset.ssStart, 10) || 0];
      if (!kid) return;
      const left = kid.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      // centre it, matching scroll-snap-align: center so the browser doesn't re-snap elsewhere
      track.scrollLeft = left - (track.clientWidth - kid.offsetWidth) / 2;
    });
  }

  /* ---- product card names: mark the ones that are ACTUALLY truncated ----
     The two-line cap is CSS (`.asp-cardtitle { max-height: 2lh; overflow: hidden }`). The FADE
     at the cut end is gated on this class, so it appears only where there is genuinely more
     name to read. A CSS-only "is the last line full?" heuristic was built first and measured
     against the real catalogue, where it failed both ways: a TRUNCATED line stops at 73% of the
     width when the next word wraps early, and a COMPLETE one ("Acoperisul Casablanca Infinity")
     reaches 91%. A fade on a whole name lies about the content.

     ⚠️ This lives HERE, not in asp-shop-filters.liquid, because that section only runs on
     collection pages — and the SEARCH results page was measured with 12 truncated names and 0
     marked, i.e. cut with no fade at all. Product cards also appear on PDPs and the homepage.
     asp-motion.js is loaded sitewide from layout/theme.liquid, so this covers every surface.

     Idempotent by construction: it returns before touching the DOM when the class is already
     right, so re-running it (resize, webfont swap, AJAX grid swap) mutates nothing. */
  /* The cut point is the MIDDLE OF LINE 2, not the start of line 3 (Vlad, 2026-09-10):
     "i don't want the cutoff to happen if it goes on line 3, i want it to happen if it goes
     past the middle of line 2". So a name may use all of line 1 and half of line 2; anything
     beyond that is faded away. Two ways to qualify:
       · the name needs a third line at all, or
       · it only needs two, but line 2 runs past 50% of the box.
     Measured off real line boxes via a Range — `scrollHeight > clientHeight` alone can only
     answer the line-3 question and is blind to the half-line rule. Note getClientRects()
     still reports line boxes that `overflow: hidden` is clipping, which is what makes the
     three-line case detectable at all. */
  const CUT_AT = 0.5;
  function markClampedTitles(root) {
    (root || document).querySelectorAll(".product-grid .asp-cardtitle").forEach((el) => {
      const box = el.getBoundingClientRect();
      let cut = false;
      if (box.width) {
        const rng = document.createRange();
        rng.selectNodeContents(el);
        const lines = [...rng.getClientRects()].filter((r) => r.width > 0);
        cut = lines.length > 2 ||
              (lines.length === 2 && lines[1].right - box.left > box.width * CUT_AT);
      }
      if (cut === el.classList.contains("is-clamped")) return;
      el.classList.toggle("is-clamped", cut);
    });
  }

  function initCardTitleClamp() {
    markClampedTitles();
    /* Again once the webfonts land: DOMContentLoaded can fire while the FALLBACK face is still
       rendering, and it wraps differently, so a name measured as fitting can overflow the moment
       the real face swaps in — cut, with no fade. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => markClampedTitles());
    }
    /* Wrapping depends on card width, so re-test after a real WIDTH change.
       ⚠️ Width only, and debounced: mobile browsers fire `resize` when the URL bar collapses
       during an ordinary scroll — reacting to that is what once yanked the sidescroll rail out
       from under a visitor's thumb (see the note on initSidescroll above). */
    let w = window.innerWidth, t;
    window.addEventListener("resize", () => {
      if (window.innerWidth === w) return;
      w = window.innerWidth;
      clearTimeout(t);
      t = setTimeout(() => markClampedTitles(), 120);
    });
  }

  window.ASP.markClampedTitles = markClampedTitles;
  window.ASP.bindMotion = bindMotion;
  /* Deliberately once, on load only — NOT on resize. Mobile browsers fire `resize`
     when the URL bar collapses during a normal page scroll, which would yank the
     rail back to its start card out from under the visitor's thumb. */
  /* initRailReveals must follow bindMotion — it un-observes what bindMotion just registered. */
  function run() {
    bindMotion(document); initHeader(); initParallax(); initPdpGallery();
    initRailReveals(); initSidescroll(); initCardTitleClamp();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
})();
